/**
 * BLRRD Batch modal — Phase 3.A.batch.v1.
 *
 * Multi-file dropzone + format/quality/dim picker + Start/Cancel +
 * progress list. v1 ships format conversion only; preset picker is
 * deferred to v1.5 (legacy editor's clarity/blur/sharpen/vignette/
 * grain effects need ESM-extraction first — Codex r1 STOP-2).
 *
 * Mounted lazily via `openBatchModal()` (called by toolbar btnBatch).
 * Uses native <dialog> like src/ui/hdr-merge-modal.js.
 *
 * @module src/ui/batch-modal
 */

let dialog = null;
let pickedFiles = [];
let activeHandle = null;

const ACCEPT_ATTR = 'image/*,.tif,.tiff,.heic,.heif,.hif,.exr,.dpx,.cr2,.cr3,.nef,.arw,.dng,.raf,.rw2,.orf,.x3f';

function el(tag, attrs = {}, children = []) {
    const node = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
        if (k === 'class') node.className = v;
        else if (k === 'style' && typeof v === 'object') Object.assign(node.style, v);
        else if (k === 'text') node.textContent = v;
        else if (k.startsWith('on') && typeof v === 'function') {
            node.addEventListener(k.slice(2), v);
        } else if (typeof v === 'boolean') {
            if (v) node.setAttribute(k, '');
        } else if (v != null) node.setAttribute(k, v);
    }
    for (const child of [].concat(children || [])) {
        if (child == null) continue;
        node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
    }
    return node;
}

function build() {
    const dlg = el('dialog', { id: 'batchModal', class: 'blrrd-batch-modal' });
    dlg.innerHTML = `
      <div class="bbm-inner">
        <div class="bbm-header">
          <span class="bbm-title">Batch convert</span>
          <span class="bbm-subtitle">Format conversion</span>
          <button class="bbm-close" type="button" aria-label="Close">×</button>
        </div>
        <div class="bbm-dropzone" id="bbmDropzone">
          <div class="bbm-dz-text">Drag image files here<br>or</div>
          <button type="button" class="bbm-dz-btn" id="bbmPickBtn">Choose files…</button>
          <input type="file" multiple accept="${ACCEPT_ATTR}" id="bbmFileInput" hidden>
          <div class="bbm-dz-hint">PNG · JPEG · WebP · AVIF · TIFF · HEIC/HEIF<br>RAW + EXR coming in Phase 3.B / 3.C</div>
        </div>
        <div class="bbm-files" id="bbmFiles"></div>
        <div class="bbm-controls">
          <label class="bbm-row">
            <span class="bbm-label">Output format</span>
            <select id="bbmFormat">
              <option value="jpeg">JPEG</option>
              <option value="png">PNG</option>
              <option value="webp">WebP</option>
            </select>
          </label>
          <label class="bbm-row" id="bbmQualityRow">
            <span class="bbm-label">Quality</span>
            <input type="range" id="bbmQuality" min="10" max="100" value="92">
            <span class="bbm-val" id="bbmQualityVal">92%</span>
          </label>
          <label class="bbm-row">
            <span class="bbm-label">Max dimension</span>
            <select id="bbmDim">
              <option value="original">Original</option>
              <option value="4k">4K (3840 px)</option>
              <option value="fhd">Full HD (1920 px)</option>
              <option value="hd">HD (1280 px)</option>
            </select>
          </label>
        </div>
        <div class="bbm-progress" id="bbmProgress" hidden>
          <div class="bbm-progress-bar"><div class="bbm-progress-fill" id="bbmProgressFill"></div></div>
          <div class="bbm-progress-text" id="bbmProgressText">0 / 0</div>
          <div class="bbm-progress-list" id="bbmProgressList"></div>
        </div>
        <div class="bbm-buttons">
          <button class="bbm-cancel" id="bbmCancel" hidden type="button">Cancel</button>
          <button class="bbm-start" id="bbmStart" type="button" disabled>Start (0 files)</button>
        </div>
      </div>`;
    document.body.appendChild(dlg);
    /* Wire interactions */
    const $ = (id) => dlg.querySelector('#' + id);
    $('bbmPickBtn').addEventListener('click', () => $('bbmFileInput').click());
    $('bbmFileInput').addEventListener('change', (e) => {
        appendFiles(Array.from(e.target.files || []));
        e.target.value = '';
    });
    const dz = $('bbmDropzone');
    dz.addEventListener('dragover', (e) => { e.preventDefault(); dz.classList.add('over'); });
    dz.addEventListener('dragleave', () => dz.classList.remove('over'));
    dz.addEventListener('drop', (e) => {
        e.preventDefault();
        dz.classList.remove('over');
        appendFiles(Array.from(e.dataTransfer.files || []));
    });
    $('bbmFormat').addEventListener('change', () => {
        const isPng = $('bbmFormat').value === 'png';
        $('bbmQualityRow').style.opacity = isPng ? '0.4' : '1';
        $('bbmQuality').disabled = isPng;
    });
    $('bbmQuality').addEventListener('input', () => {
        $('bbmQualityVal').textContent = $('bbmQuality').value + '%';
    });
    $('bbmCancel').addEventListener('click', () => {
        if (activeHandle && typeof activeHandle.cancel === 'function') {
            activeHandle.cancel();
        }
    });
    $('bbmStart').addEventListener('click', () => runBatch(dlg));
    dlg.querySelector('.bbm-close').addEventListener('click', () => closeModal());
    dlg.addEventListener('cancel', (e) => {
        e.preventDefault();
        if (activeHandle) {
            activeHandle.cancel();
        } else {
            closeModal();
        }
    });
    return dlg;
}

function appendFiles(files) {
    const exts = /\.(jpe?g|png|webp|avif|gif|bmp|tif|tiff|heic|heif|hif)$/i;
    /* RAW + EXR + DPX still throw UnsupportedFormatError at decode, so
     * we accept them in the picker (consistent with single-file UX)
     * but the per-file run will fail with a friendly error. */
    const allExts = /\.(jpe?g|png|webp|avif|gif|bmp|tif|tiff|heic|heif|hif|exr|dpx|cr2|cr3|nef|arw|dng|raf|rw2|orf|x3f)$/i;
    const accepted = files.filter((f) => {
        if (!f) return false;
        if (f.type && f.type.startsWith('image/')) return true;
        return allExts.test(f.name || '');
    });
    pickedFiles.push(...accepted);
    renderFiles();
    if (accepted.length < files.length) {
        const dropped = files.length - accepted.length;
        toastIfAvailable(`${dropped} file(s) skipped — not a supported image type`, 'warn');
    }
    void exts;
}

function renderFiles() {
    if (!dialog) return;
    const list = dialog.querySelector('#bbmFiles');
    list.replaceChildren();
    for (let i = 0; i < pickedFiles.length; i++) {
        const f = pickedFiles[i];
        const row = el('div', { class: 'bbm-file-row' }, [
            el('span', { class: 'bbm-file-name', text: f.name }),
            el('span', { class: 'bbm-file-size', text: formatSize(f.size) }),
            el('button', {
                class: 'bbm-file-remove',
                type: 'button',
                'aria-label': 'Remove',
                text: '×',
                onclick: () => {
                    pickedFiles.splice(i, 1);
                    renderFiles();
                },
            }),
        ]);
        list.appendChild(row);
    }
    const startBtn = dialog.querySelector('#bbmStart');
    startBtn.disabled = pickedFiles.length === 0;
    startBtn.textContent = `Start (${pickedFiles.length} file${pickedFiles.length === 1 ? '' : 's'})`;
}

function formatSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB';
    return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB';
}

function toastIfAvailable(msg, kind) {
    if (typeof window.toast === 'function') {
        try { window.toast(msg, kind); } catch { /* ignore */ }
    } else {
        console.log('[blrrd-batch]', kind, msg);
    }
}

async function runBatch(dlg) {
    if (activeHandle) return;
    if (pickedFiles.length === 0) return;
    if (!window.BlrrdBatch) {
        toastIfAvailable('Batch module not loaded', 'err');
        return;
    }

    const $ = (id) => dlg.querySelector('#' + id);
    const format  = $('bbmFormat').value;
    const quality = parseInt($('bbmQuality').value, 10) / 100;
    const dim     = $('bbmDim').value;

    /* Lock UI. */
    $('bbmStart').disabled = true;
    $('bbmStart').textContent = 'Working…';
    $('bbmCancel').hidden = false;
    $('bbmProgress').hidden = false;
    const fillEl = $('bbmProgressFill');
    const textEl = $('bbmProgressText');
    const listEl = $('bbmProgressList');
    listEl.replaceChildren();
    fillEl.style.width = '0%';
    textEl.textContent = `0 / ${pickedFiles.length}`;

    activeHandle = window.BlrrdBatch.start(pickedFiles, {
        outputFormat: format,
        outputQuality: quality,
        outputDim: dim,
    });

    /* Tap event stream for progress UI. */
    (async () => {
        let done = 0;
        for await (const ev of activeHandle) {
            if (ev.type === 'file-done' || ev.type === 'file-error') {
                done++;
                fillEl.style.width = ((done / pickedFiles.length) * 100).toFixed(1) + '%';
                textEl.textContent = `${done} / ${pickedFiles.length}`;
                const row = el('div', {
                    class: 'bbm-progress-row ' + (ev.error ? 'err' : 'ok'),
                }, [
                    el('span', { class: 'bbm-progress-icon', text: ev.error ? '✗' : '✓' }),
                    el('span', { class: 'bbm-progress-name', text: ev.name }),
                    ev.error ? el('span', { class: 'bbm-progress-err', text: ev.error }) : null,
                ]);
                listEl.appendChild(row);
                listEl.scrollTop = listEl.scrollHeight;
            } else if (ev.type === 'cancelled') {
                listEl.appendChild(el('div', { class: 'bbm-progress-row warn',
                    text: 'Cancelled.' }));
            }
        }
    })();

    let result;
    try {
        result = await activeHandle.done;
    } catch (err) {
        toastIfAvailable('Batch failed: ' + (err.message || err), 'err');
        activeHandle = null;
        $('bbmStart').disabled = false;
        $('bbmStart').textContent = `Start (${pickedFiles.length} files)`;
        $('bbmCancel').hidden = true;
        return;
    }

    /* Build ZIP if any output blobs survived. */
    const success = result.outputs.filter((o) => o.blob);
    if (success.length === 0) {
        toastIfAvailable(result.cancelled ? 'Cancelled.' : 'No files produced.', 'warn');
        activeHandle = null;
        $('bbmStart').disabled = false;
        $('bbmStart').textContent = `Start (${pickedFiles.length} files)`;
        $('bbmCancel').hidden = true;
        return;
    }

    /* Lazy-load JSZip + emit a single download. */
    try {
        const JSZip = window.JSZip || await new Promise((res, rej) => {
            const s = document.createElement('script');
            s.src = '/vendor/jszip/jszip.min.js';
            s.onload = () => window.JSZip ? res(window.JSZip) : rej(new Error('JSZip load failed'));
            s.onerror = () => rej(new Error('JSZip script load failed'));
            document.head.appendChild(s);
        });
        const zip = new JSZip();
        for (const o of success) zip.file(o.name, o.blob, { compression: 'STORE' });
        const blob = await zip.generateAsync({ type: 'blob' });
        const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..*/, '').replace('T', '-');
        const a = el('a', {
            href: URL.createObjectURL(blob),
            download: `blrrd-batch-${stamp}.zip`,
            style: { display: 'none' },
        });
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            URL.revokeObjectURL(a.href);
            a.remove();
        }, 60000);
        toastIfAvailable(`Batch complete: ${success.length} file${success.length === 1 ? '' : 's'} → ZIP`, 'ok');
    } catch (err) {
        toastIfAvailable('ZIP build failed: ' + (err.message || err), 'err');
    }
    activeHandle = null;
    $('bbmStart').disabled = false;
    $('bbmStart').textContent = `Start (${pickedFiles.length} files)`;
    $('bbmCancel').hidden = true;
}

function closeModal() {
    if (activeHandle) activeHandle.cancel();
    if (!dialog) return;
    if (dialog.open) dialog.close();
    pickedFiles = [];
    renderFiles();
    const dlg = dialog;
    dlg.querySelector('#bbmProgress').hidden = true;
    dlg.querySelector('#bbmProgressList').replaceChildren();
}

export function openBatchModal() {
    if (!dialog) dialog = build();
    if (!dialog.open) dialog.showModal();
}
